import { useMemo, useState } from 'react';

import {
  type Track,
  trackFilename,
  trackStats,
  trackToGpx,
} from '../core/track';
import type { TrackSummary } from '../storage/trackStore';
import {
  formatDistance,
  formatDuration,
  formatSpeed,
  type UnitPreferences,
} from '../core/units';

export interface TrackPanelProps {
  /** The track being recorded, or null when not recording. */
  recording: Track | null;
  /** A saved track opened for viewing, or null. */
  viewing: Track | null;
  /** Saved tracks, most recent first, without their points. */
  saved: readonly TrackSummary[];
  units: UnitPreferences;
  /** True while the position being recorded is a simulated one. */
  simulating: boolean;
  /** True when there is no position to record at all. */
  noFix: boolean;
  onStart: () => void;
  onStop: () => Promise<void> | void;
  onRename: (name: string) => void;
  onOpen: (id: string) => Promise<void> | void;
  onCloseViewed: () => void;
  onDelete: (id: string) => Promise<void> | void;
  onClose: () => void;
}

const stamp = (ms: number): string => new Date(ms).toLocaleString(undefined, {
  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
});

/** Stable, so the stats memo below does not recompute on every render. */
const NO_POINTS = { points: [] };

/**
 * Track recording, and what has been recorded before.
 *
 * A track is a record, not a plan, and the panel is built around that
 * difference: there is nothing here that edits the line. What it does offer is
 * the way back off the phone — GPX, which every chartplotter and OpenCPN reads —
 * because a track that can only be looked at here is barely worth keeping.
 *
 * A recording made while the debug simulation is on is labelled as invented
 * everywhere it appears, including in the file that leaves the device. There is
 * no way to un-mark one.
 */
export function TrackPanel({
  recording,
  viewing,
  saved,
  units,
  simulating,
  noFix,
  onStart,
  onStop,
  onRename,
  onOpen,
  onCloseViewed,
  onDelete,
  onClose,
}: TrackPanelProps): React.JSX.Element {
  const [error, setError] = useState<string | null>(null);
  const stats = useMemo(
    () => trackStats(recording ?? viewing ?? NO_POINTS),
    [recording, viewing],
  );
  const shown = recording ?? viewing;

  const stop = async (): Promise<void> => {
    try {
      setError(null);
      await onStop();
    } catch {
      setError('Could not save this track. It is still in memory — try again.');
    }
  };

  /**
   * Write the track out as GPX.
   *
   * An object URL and a synthetic click, because there is no server to POST to
   * and none is wanted. iOS Safari opens it in a viewer rather than downloading
   * it, which is unhelpful but not lossy — the text is all there to share from.
   */
  const exportGpx = (track: Track): void => {
    try {
      setError(null);
      const blob = new Blob([trackToGpx(track)], { type: 'application/gpx+xml' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = trackFilename(track);
      link.click();
      // Revoked on the next turn of the loop: revoking synchronously races the
      // browser's own fetch of the URL in some engines and yields an empty file.
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch {
      setError('Could not write the GPX file.');
    }
  };

  return (
    <div className="sheet sheet--track" role="dialog" aria-label="Track">
      <div className="sheet__header">
        {recording ? (
          <input
            className="route__name"
            value={recording.name}
            onChange={(e) => onRename(e.target.value)}
            aria-label="Track name"
            placeholder="Track name"
          />
        ) : (
          <h2>Track</h2>
        )}
        <button type="button" onClick={onClose} aria-label="Close">✕</button>
      </div>

      {recording ? (
        <>
          <p className="track__recording" role="status">
            <span className="track__dot" aria-hidden="true" />
            Recording{simulating ? ' a SIMULATED position' : ''}
          </p>
          {recording.simulated && (
            <p className="route__warning" role="alert">
              This track contains made-up positions and is marked as simulated
              wherever it appears, including in the exported file. It is not a
              record of where a boat has been.
            </p>
          )}
          {noFix && (
            <p className="route__warning" role="alert">
              No position fix, so nothing is being recorded. The track will
              resume by itself when the receiver reports again.
            </p>
          )}
        </>
      ) : (
        <p className="sheet__hint">
          Records where the boat actually goes, about once every few seconds and
          more sparingly when it is not moving. Kept on this device; nothing is
          uploaded.
        </p>
      )}

      {shown && (
        <dl className="route__summary">
          <div>
            <dt>Distance</dt>
            <dd>{formatDistance(stats.distanceM, units.distance)}</dd>
          </div>
          <div>
            <dt>Elapsed</dt>
            <dd>{formatDuration(stats.durationS)}</dd>
          </div>
          <div>
            <dt>Top speed</dt>
            <dd>
              {stats.maxSpeedMs === null
                ? '—'
                : formatSpeed(stats.maxSpeedMs, units.speed)}
            </dd>
          </div>
        </dl>
      )}

      {shown && stats.points > 0 && (
        <p className="sheet__hint sheet__hint--small">
          {stats.points} points.
          {stats.averageSpeedMs !== null && (
            <> Speed made good over the whole recording, stops included,{' '}
              {formatSpeed(stats.averageSpeedMs, units.speed)}.
            </>
          )}
        </p>
      )}

      <div className="route__actions">
        {recording ? (
          <button type="button" onClick={() => void stop()}>Stop and save</button>
        ) : (
          <button type="button" onClick={onStart}>Start recording</button>
        )}
        {shown && shown.points.length > 0 && (
          <button type="button" onClick={() => exportGpx(shown)}>Export GPX</button>
        )}
        {viewing && !recording && (
          <button type="button" onClick={onCloseViewed}>Hide from chart</button>
        )}
      </div>

      {error && <p className="route__warning" role="alert">{error}</p>}

      {saved.length > 0 && (
        <>
          <h3 className="route__saved-heading">Saved tracks</h3>
          <ul className="route__saved">
            {saved.map((summary) => (
              <li key={summary.id}>
                <button
                  type="button"
                  className="route__saved-open"
                  onClick={() => void onOpen(summary.id)}
                  aria-current={viewing?.id === summary.id ? 'true' : undefined}
                >
                  <span className="route__saved-name">
                    {summary.name}
                    {summary.simulated && <span className="track__sim"> SIMULATED</span>}
                  </span>
                  <span className="route__saved-meta">
                    {stamp(summary.startedAt)} ·{' '}
                    {formatDistance(summary.distanceM, units.distance)} ·{' '}
                    {formatDuration(summary.durationS)}
                  </span>
                </button>
                <button
                  type="button"
                  className="route__saved-delete"
                  onClick={() => void onDelete(summary.id)}
                  aria-label={`Delete ${summary.name}`}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
